import { createI18n } from 'vue-i18n'
import { ref } from 'vue'
import { browserLocale, defaultLocale, normalizeLocale, supportedLocales, type Locale } from './locales'
import routes from '@/content/localizedRoutes.json'
const messageFiles = import.meta.glob('./messages/*.json', { eager: true, import: 'default' }) as Record<string, Record<string, string>>
const messages = Object.fromEntries(supportedLocales.map(locale => [locale.id, messageFiles[`./messages/${locale.id}.json`]])) as Record<Locale, Record<string, string>>

const preferenceKey = 'camplist.locale'
function storedChoice() { try { return normalizeLocale(localStorage.getItem(preferenceKey)) } catch { return undefined } }
export const explicitLocale = ref<Locale | undefined>(storedChoice())
const locationHint = normalizeLocale(new URLSearchParams(location.search).get('lang'))
export const activeLocale = ref<Locale>(publicLocale(location.pathname) || locationHint || namespaceLocale(location.pathname) || explicitLocale.value || browserLocale())
export const i18n = createI18n({ legacy: false, locale: activeLocale.value, fallbackLocale: defaultLocale, messages, missingWarn: import.meta.env.DEV, fallbackWarn: false })
export function setLocale(locale: Locale) {
  activeLocale.value = locale
  i18n.global.locale.value = locale
  document.documentElement.lang = locale
}
export function rememberLocale(locale: Locale) {
  explicitLocale.value = locale
  try { localStorage.setItem(preferenceKey, locale) } catch { /* Selection still works with storage disabled. */ }
  setLocale(locale)
}
export function publicLocale(path: string): Locale | undefined {
  const normalized = path.replace(/\/$/, '') || '/'
  return supportedLocales.find(locale => routes.some(route => (route[locale.id].replace(/\/$/, '') || '/') === normalized))?.id
}
export function publicRoute(path: string) { const normalized = path.replace(/\/$/, '') || '/'; return routes.find(route => supportedLocales.some(locale => (route[locale.id].replace(/\/$/, '') || '/') === normalized)) }
export function namespaceLocale(path: string): Locale | undefined {
  const home = routes.find(route => route.id === 'home')!
  return supportedLocales.find(locale => home[locale.id] !== '/' && path.startsWith(home[locale.id]))?.id
}
export function localizedPath(path: string, locale: Locale = activeLocale.value) {
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/api/') || path.startsWith('/hubs/')) return path
  const url = new URL(path, location.origin)
  const route = publicRoute(url.pathname)
  if (route) { url.pathname = route[locale]; url.searchParams.delete('lang') }
  else url.searchParams.set('lang', locale)
  return url.pathname + url.search + url.hash
}
export function applySavedLocale(saved?: string | null) {
  if (!publicLocale(location.pathname) && !normalizeLocale(new URLSearchParams(location.search).get('lang')) && !explicitLocale.value) setLocale(normalizeLocale(saved) || browserLocale())
}
// Message resources use flat stable keys, including dots in catalogue IDs.
export function hasMessage(key: string) { return Object.prototype.hasOwnProperty.call(i18n.global.getLocaleMessage(activeLocale.value), key) }
export function t(key: string, values: Record<string, unknown> = {}) { return i18n.global.t(key, values) }
export function plural(key: string, count: number, values: Record<string, unknown> = {}) { return i18n.global.t(key, { ...values, count }, count) }
export function formatDate(value: Date | number, options: Intl.DateTimeFormatOptions = {}) { return new Intl.DateTimeFormat(activeLocale.value, options).format(value) }
export function searchText(value: string) { return value.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase(activeLocale.value) }
setLocale(activeLocale.value)
